// Company — SPEC-001 §12: per-clinic profile (7 groups, 20 fields), edit,
// banking reveal (R-17), and Drive Documents / Vendors / Management.
//
// docs/company-page-requirements.md is the gap this file closes: the live
// Apps Script app groups the profile into Identity / Location / Payroll /
// Banking / Processing / Systems / Manager, plus a bare Notes paragraph, and
// this page now renders all of it instead of the five fields the rebuild
// shipped with. STRUCTURE AND DATA only came from that doc — the markup is
// People's (R-16: People is the design spec for the whole site), never the
// live app's.
//
// Access is server-side. Managers may GET /api/v1/company (L-18) and see
// their clinic; the banking key is omitted rather than masked. OWNER and
// DIRECTOR also get a Management tab. A manager with one clinic gets no
// strip (ClinicTabs) and no Management tab.
//
// `employee_secure` fields never appear here — this page is company banking
// (`company_secure`), a separate table under the same discipline (R-17): GET
// returns a MASK only, and the real numbers are fetched (and logged) one
// reveal at a time, kept in component state, never written into the shared
// resource cache — so navigating away drops them (they are not cached data,
// they are the result of a logged action).
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from '../components/SortableTh';
import { authedGet, authedGetBlob, authedWrite, ApiError } from '../lib/apiClient';
import { API_BASE } from '../lib/config';
import { authToken } from '../lib/auth';
import { withViewAs } from '../lib/viewAs';
import { FaultState, EmptyState } from '../components/FaultState';
import { OpenFolderLink } from '../components/OpenFolderLink';
import { ClinicTabs, visibleClinics, activeClinicFrom, canSeeManagementTab } from '../components/ClinicTabs';
import { FavoriteStar } from '../components/FavoriteStar';
import { CompanyBankingEdit, type BankingMasks } from '../components/CompanyBankingEdit';
import { CompanyVendors } from '../components/CompanyVendors';
import { BonusTracker } from '../components/BonusTracker';
import { ServicePoints } from '../components/ServicePoints';
import { CompanyLicenses } from '../components/CompanyLicenses';
import { CompanyLeases } from '../components/CompanyLeases';
import { CompanyInsurance } from '../components/CompanyInsurance';
import { CompanyCommunications } from '../components/CompanyCommunications';
import { CompanyIntake } from '../components/CompanyIntake';
import { MoneyPage } from './Money';
import { ReportsPage } from './Reports';
import { legacyRedirect, replaceRoute } from '../lib/router';
import { formatDate } from '../lib/date';
import { session } from '../lib/session';
import { useResource, refetchResource, peekResource, persistResource } from '../lib/store';
import {
  COMPANY_HR_KEY, companyDriveKey, fetchCompanyHr, fetchCompanyDrivePane,
} from '../lib/companyDrive';

const COMPANY_KEY = 'company';
export { COMPANY_HR_KEY };
const EM_DASH = '—';

const HR_MISC_LOCATION: Record<string, string> = {
  'LM HR Misc': 'MELM',
  'VL HR Misc': 'MEVL',
  'WG HR Misc': 'MEWG',
  'WP HR Misc': 'MEWP',
};

function canWriteCompanyDrive(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR' || me.role === 'MANAGER'));
}

function canAddHrDocuments(topicName: string): boolean {
  const me = session.value.me;
  if (!canWriteCompanyDrive() || !me) return false;
  if (me.role === 'OWNER' || me.role === 'DIRECTOR') return true;
  const code = HR_MISC_LOCATION[topicName];
  return Boolean(code && me.locations?.includes(code));
}

function canAddClinicDrive(location?: string): boolean {
  const me = session.value.me;
  if (!canWriteCompanyDrive() || !me || !location) return false;
  if (me.role === 'OWNER' || me.role === 'DIRECTOR') return true;
  return Boolean(me.locations?.includes(location));
}

async function uploadHrDocument(topicName: string, file: File): Promise<void> {
  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs('/api/v1/company/drive/hr-files')}`, {
    method: 'POST',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-hr-topic': topicName,
      'idempotency-key': crypto.randomUUID(),
    },
    body: file,
  });
  const body = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
}

type ClinicDriveUpload = 'documents' | 'licenses' | 'insurance' | 'manager';

async function uploadClinicDocument(pane: ClinicDriveUpload, location: string, file: File): Promise<void> {
  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs('/api/v1/company/drive/files')}`, {
    method: 'POST',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-company-pane': pane,
      'x-location': location,
      'idempotency-key': crypto.randomUUID(),
    },
    body: file,
  });
  const body = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
}

async function deleteClinicDocument(pane: ClinicDriveUpload, location: string, fileId: string): Promise<void> {
  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs(`/api/v1/company/drive/files/${encodeURIComponent(fileId)}`)}`, {
    method: 'DELETE',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'x-company-pane': pane,
      'x-location': location,
      'idempotency-key': crypto.randomUUID(),
    },
  });
  const body = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
}

async function openClinicDocument(pane: ClinicDriveUpload, location: string, fileId: string): Promise<string> {
  const params = new URLSearchParams({ pane, location });
  const data = await authedGet<{ url?: string }>(
    `/api/v1/company/drive/files/${encodeURIComponent(fileId)}?${params.toString()}`,
  );
  if (!data.url) throw new Error('not found');
  return data.url;
}

async function dropDriveFile(location: string, fileId: string): Promise<void> {
  const key = companyDriveKey('documents', location);
  const current = peekResource<CompanyDriveData>(key);
  if (!current) return;
  await persistResource(key, {
    ...current,
    sections: (current.sections || []).map((section) => ({
      ...section,
      files: section.files.filter((f) => f.id !== fileId),
    })),
  });
}

export interface CompanyBanking {
  bank_routing: string | null;
  bank_account: string | null;
}

export interface CompanyProfile {
  id: number;
  company_code: string;
  legal_name: string;
  legal_entity: string | null;
  ein: string | null;
  opened_date: string | null;
  acquired_date: string | null;
  address: string | null;
  phone: string | null;
  phone_provider: string | null;
  hours: string | null;
  adp_account: string | null;
  rt_sui_account: string | null;
  sui_effective_date: string | null;
  merchant_id: string | null;
  amex_merchant_id: string | null;
  mm_number: string | null;
  ip_address: string | null;
  manager_name: string | null;
  manager_email: string | null;
  notes: string | null;
  /** Absent entirely for a MANAGER — the server omits the key rather than
   * sending nulls, so this page cannot render a section there is no data for
   * (owner ruling, 2026-08-17). */
  banking?: CompanyBanking;
  updated_at: string;
}

export interface CompanyDocument {
  id: number;
  company_code: string;
  kind: string;
  file_name: string;
  url: string | null;
  uploaded_at: string;
}

interface CompanyResponse {
  profiles: CompanyProfile[];
  documents: CompanyDocument[];
}

/** The three real `date` columns — rendered via formatDate, edited with a
 * `type="date"` input (the raw YYYY-MM-DD string is already that input's
 * native value shape, so no reformatting is needed going into edit mode). */
const DATE_FIELDS = new Set(['opened_date', 'acquired_date', 'sui_effective_date']);

type ProfileFieldKey = Exclude<keyof CompanyProfile, 'id' | 'company_code' | 'legal_name' | 'banking' | 'updated_at'>;

/** The seven groups, in the requirements doc's order, mirroring the server's
 * own write whitelist (src/company.js COMPANY_PROFILE_FIELDS) minus
 * `legal_name` (the card heading, not a group field) and `notes` (rendered
 * separately as prose, not a labelled chip). Banking is NOT here — it is not
 * part of this whitelist at all (R-17) and gets its own render path below. */
const PROFILE_GROUPS: { title: string; fields: [ProfileFieldKey, string][] }[] = [
  {
    title: 'Identity',
    fields: [
      ['legal_entity', 'Legal entity'],
      ['ein', 'EIN'],
      ['opened_date', 'Opened'],
      ['acquired_date', 'Acquired'],
    ],
  },
  {
    title: 'Location',
    fields: [
      ['address', 'Address'],
      ['phone', 'Phone'],
      ['phone_provider', 'Phone provider'],
      ['hours', 'Hours'],
    ],
  },
  {
    title: 'Payroll',
    fields: [
      ['adp_account', 'ADP account'],
      ['rt_sui_account', 'RT/SUI account'],
      ['sui_effective_date', 'SUI effective date'],
    ],
  },
  {
    title: 'Processing',
    fields: [
      ['merchant_id', 'Merchant ID'],
      ['amex_merchant_id', 'Amex merchant ID'],
    ],
  },
  {
    title: 'Systems',
    fields: [
      ['mm_number', 'MM#'],
      ['ip_address', 'IP address'],
    ],
  },
  {
    title: 'Manager',
    fields: [
      ['manager_name', 'Name'],
      ['manager_email', 'Email'],
    ],
  },
];

const ALL_EDITABLE_FIELDS: ProfileFieldKey[] = [
  ...PROFILE_GROUPS.flatMap((g) => g.fields.map(([key]) => key)),
  'notes',
];

// --- routing number padding (requirements doc §1) ----------------------------

export interface PaddedRouting {
  /** The value to display — padded when short, untouched when 9 or more
   * digits long. Never auto-corrected past 9 digits (see `warning`). */
  value: string;
  /** True when the input is LONGER than 9 digits — shown as-is, flagged, and
   * never silently trimmed or "fixed". */
  warning: boolean;
}

/**
 * Pad a bank routing number back to 9 digits with leading zeros.
 *
 * A spreadsheet coerced "063000047" to the number 63000047 once, and the
 * live app pads it back on display rather than storing it wrong forever. A
 * value already 9 digits is returned unchanged; a value LONGER than 9 is
 * returned unchanged too, with `warning: true` — never auto-corrected,
 * because a routing number with 10+ digits is a data problem, not a
 * formatting one, and guessing which digit to drop would be worse than
 * showing it plainly wrong.
 */
export function padRoutingNumber(raw: string): PaddedRouting {
  if (raw.length > 9) return { value: raw, warning: true };
  if (raw.length < 9) return { value: raw.padStart(9, '0'), warning: false };
  return { value: raw, warning: false };
}

// --- document search (INV-1: filters already-fetched file names in memory) --

/** A minimum of 2 characters before anything filters — short of that, every
 * keystroke would otherwise thrash the table for no useful narrowing. */
const MIN_SEARCH_CHARS = 2;

function isSearchActive(query: string): boolean {
  return query.trim().length >= MIN_SEARCH_CHARS;
}

function matchesDriveFileName(name: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length < MIN_SEARCH_CHARS) return true;
  return name.toLowerCase().includes(q);
}

// Owner, 2026-10-06: Money and Reports moved in as Company tabs ("Id like to
// move reports and Money under company as a sub menu item"); HR moved out to
// People ("id like to move HR and the SPC as a menu item in People").
type CompanySection = 'profile' | 'operations' | 'money' | 'reports' | 'documents' | 'folder' | 'communications' | 'intake';
type DocDisk = 'misc' | 'licenses' | 'insurance';
/** The Management menu's own disks (owner, 2026-09-26: "Leases should be in
 * disk under the Management menu"; same evening, same shape for Insurance —
 * "Let's get this into a disk called Insurance under Company and
 * Management … same as we did with Leases"): the Management Drive files,
 * the Leases table, and the Insurance table. Owner/director only, like the
 * Management tab itself. */
type MgmtDisk = 'files' | 'leases' | 'insurance';
function mgmtDiskFromHash(): MgmtDisk {
  const h = (window.location.hash || '').slice(1);
  if (h === 'leases' || h.startsWith('leases-')) return 'leases';
  if (h === 'insurance' || h.startsWith('insurance-')) return 'insurance';
  return 'files';
}
type FolderChip = 'files' | 'bonus';
type OpsChip = 'wufoo' | 'vendors';

const COMPANY_SECTIONS: CompanySection[] = [
  'profile', 'operations', 'money', 'reports', 'documents', 'folder', 'communications', 'intake',
];
/** Sections whose content is not a Drive file list — no "Search company
 * folder" box beside the tabs. */
const NO_DOC_SEARCH = new Set<CompanySection>(['profile', 'operations', 'money', 'reports', 'communications', 'intake']);
/** Money's own menu hashes (pages/Money.tsx MONEY_MENU) — Quick Docs pins
 * still say `/money#deposits`; lib/router.ts carries the hash across. */
const MONEY_HASHES = new Set(['deposits', 'petty', 'other']);
const FOLDER_CHIPS: { id: FolderChip; label: string }[] = [
  { id: 'files', label: 'Folder' },
  { id: 'bonus', label: 'Bonus Tracker' },
];
// SPC moved to People (owner, 2026-10-06), leaving Wufoo Form and Vendors.
const OPS_CHIPS: { id: OpsChip; label: string }[] = [
  { id: 'wufoo', label: 'Wufoo Form' },
  { id: 'vendors', label: 'Vendors' },
];
const DOC_DISKS: {
  id: DocDisk;
  label: string;
  section: string;
  upload?: ClinicDriveUpload;
}[] = [
  // Owner, 2026-10-06: "move mis docs in company after License and
  // Insurance". Opening Documents selects the first disk shown (Licenses);
  // a hash that names a disk still wins (hashToCompany).
  { id: 'licenses', label: 'Licenses', section: 'Licenses', upload: 'licenses' },
  { id: 'insurance', label: 'Insurance', section: 'Insurance', upload: 'insurance' },
  { id: 'misc', label: 'Misc Docs', section: 'Documents', upload: 'documents' },
];
const FIRST_DOC_DISK: DocDisk = DOC_DISKS[0].id;
const MY_FOLDER_DISK = { label: 'My Folder', section: 'My Folder', upload: 'manager' as const };

function hashToCompany(): {
  management: boolean; section: CompanySection; docDisk: DocDisk;
  folderChip: FolderChip; opsChip: OpsChip; vendorId: number | null; commId: number | null;
} {
  const h = window.location.hash.replace(/^#/, '');
  const vendor = /^vendor-(\d+)$/.exec(h);
  if (vendor) {
    return { management: false, section: 'operations', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'vendors', vendorId: Number(vendor[1]), commId: null };
  }
  if (h === 'vendors') {
    return { management: false, section: 'operations', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'vendors', vendorId: null, commId: null };
  }
  if (
    h === 'management' || h === 'leases' || h.startsWith('leases-')
    || h === 'insurance' || h.startsWith('insurance-')
  ) {
    return { management: true, section: 'profile', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  // N-095: Operations → Licenses (the real table) is a DIFFERENT hash from
  // Documents → Licenses disk (the raw Drive folder listing) below — the
  // latter is `licenses`, kept as-is so any bookmark/favorite made before
  // N-095 still lands on the Drive listing rather than silently retargeting.
  // Owner, 2026-09-10: "its supposed to be only in documents" — the Licenses
  // table lives under Documents → Licenses; the short-lived Operations hash
  // still lands there.
  if (h === 'licenses-table') {
    return { management: false, section: 'documents', docDisk: 'licenses', folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  // `insurance` itself is claimed by the Management disk above now (owner,
  // 2026-09-26 — same request as Leases, same shape): the Documents →
  // Insurance DRIVE PANE (DOC_DISKS below, the raw per-clinic folder
  // listing, unrelated to the structured table) is reached today only by
  // clicking its own chip (which sets state, never the hash — see
  // DOC_DISKS.map's onClick below), never by a hash, so nothing that
  // actually worked stops working; only a hand-typed `#insurance` URL,
  // which used to land here, now opens the new disk instead.
  if (h === 'licenses') {
    return { management: false, section: 'documents', docDisk: h, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  // `#documents` is the "Company documents" Quick Docs pin — a FOLDER pin,
  // so it keeps opening the Drive folder listing (Misc Docs), not the
  // Licenses table that now comes first.
  if (h === 'documents' || h === 'misc-docs') {
    return { management: false, section: 'documents', docDisk: 'misc', folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  if (h === 'bonus') {
    return { management: false, section: 'folder', docDisk: FIRST_DOC_DISK, folderChip: 'bonus', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  if (h === 'wufoo' || h === 'points') {
    return { management: false, section: 'operations', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  // `hr`, `compliance` and `service-compliance` moved to People (owner,
  // 2026-10-06); CompanyPage hands those hashes to /people (lib/router.ts
  // legacyRedirect) before this ever matters.
  if (MONEY_HASHES.has(h)) {
    return { management: false, section: 'money', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  if (h.startsWith('report.')) {
    return { management: false, section: 'reports', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  // N-094 ("forward and file"): a single communication's deep link, same
  // shape as `vendor-<id>` above — used by CommunicationsPanel's row links
  // and by CompanyCommunications' own onOpen.
  const comm = /^comm-(\d+)$/.exec(h);
  if (comm) {
    return { management: false, section: 'communications', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: Number(comm[1]) };
  }
  if ((COMPANY_SECTIONS as readonly string[]).includes(h)) {
    return { management: false, section: h as CompanySection, docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
  }
  return { management: false, section: 'profile', docDisk: FIRST_DOC_DISK, folderChip: 'files', opsChip: 'wufoo', vendorId: null, commId: null };
}

interface CompanyDriveFile {
  id: string;
  name: string;
  mimeType: string | null;
  webViewLink: string | null;
  modifiedTime: string | null;
}

interface CompanyDriveSection {
  name: string;
  files: CompanyDriveFile[];
  reason?: string;
  /** Only set when this caller was allowed to list that folder. */
  folderUrl?: string;
  folderModified?: string;
}

interface CompanyHrTopic {
  name: string;
  configured: boolean;
  sections: CompanyDriveSection[];
  reason?: string;
  folderUrl?: string;
}

interface CompanyHrData {
  configured: boolean;
  topics: CompanyHrTopic[];
  reason?: string;
  folderUrl?: string;
}

interface CompanyDriveData {
  configured: boolean;
  sections: CompanyDriveSection[];
  reason?: string;
}

export interface NhpRow {
  key: string;
  title: string;
  section: string;
  files: CompanyDriveFile[];
  folderUrl?: string;
  webViewLink: string | null;
}

/** Leading digits already in the Drive name — keep those, do not renumber. */
export function leadingNhpNumber(name: string): number | null {
  const m = name.trim().match(/^(\d+)/);
  return m ? Number(m[1]) : null;
}

/** 1 → 01 so 01…09 sit before 10 in Drive and on the page. */
export function padNhpNumber(n: number): string {
  return String(n).padStart(2, '0');
}

/** Show a Drive name with a two-digit leading number when it has one. */
export function nhpDisplayName(name: string): string {
  const n = leadingNhpNumber(name);
  if (n === null) return name.trim();
  return name.trim().replace(/^\d+/, padNhpNumber(n));
}

/** Folder title as shown — Drive's name, with a single-digit prefix padded. */
export function nhpDisplayTitle(name: string, _index?: number): string {
  return nhpDisplayName(name);
}

/** The shared pack every manager uses — not a chip, the default HR list. */
export function isSharedNhpTopic(name: string): boolean {
  const n = name.trim().toLowerCase();
  return n === 'nhp' || n === 'new hire paperwork';
}

function compareNhpNames(a: string, b: string): number {
  const na = leadingNhpNumber(a);
  const nb = leadingNhpNumber(b);
  if (na !== null && nb !== null && na !== nb) return na - nb;
  if (na !== null && nb === null) return -1;
  if (na === null && nb !== null) return 1;
  return a.localeCompare(b);
}

/** One row per file, still grouped by the Drive folder it sat in.
 * Folder order stays as Drive sent it. Files inside a folder sort by
 * the number already in the file name (1, 2, 10 — not 1, 10, 2). */
export function flattenHrRows(topicName: string, sections: CompanyDriveSection[]): NhpRow[] {
  const rows: NhpRow[] = [];
  const ordered = [...sections].sort((a, b) => compareNhpNames(a.name, b.name));
  for (const s of ordered) {
    const section = s.name === topicName ? '' : s.name;
    const files = [...s.files].sort((a, b) => compareNhpNames(a.name, b.name));
    for (const f of files) {
      rows.push({
        key: f.id,
        title: f.name,
        section,
        files: [f],
        folderUrl: s.folderUrl,
        webViewLink: f.webViewLink,
      });
    }
  }
  return rows;
}

function groupNhpRows(rows: NhpRow[]): { name: string; rows: NhpRow[] }[] {
  const groups: { name: string; rows: NhpRow[] }[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.name === row.section) last.rows.push(row);
    else groups.push({ name: row.section, rows: [row] });
  }
  return groups;
}

// --- the page -----------------------------------------------------------------

export function CompanyPage(): JSX.Element {
  const start = hashToCompany();
  const [clinic, setClinic] = useState<string | null>(null);
  const [management, setManagement] = useState(start.management);
  const [mgmtDisk, setMgmtDisk] = useState<MgmtDisk>(() => mgmtDiskFromHash());
  const [section, setSection] = useState<CompanySection>(start.section);
  const [docDisk, setDocDisk] = useState<DocDisk>(start.docDisk);
  const [folderChip, setFolderChip] = useState<FolderChip>(start.folderChip);
  const [opsChip, setOpsChip] = useState<OpsChip>(start.opsChip);
  const [vendorId, setVendorId] = useState<number | null>(start.vendorId);
  const [commId, setCommId] = useState<number | null>(start.commId);
  const [docQuery, setDocQuery] = useState('');
  const company = useResource<CompanyResponse>(COMPANY_KEY, () => authedGet('/api/v1/company'));

  useEffect(() => {
    // HR and SPC moved to People (owner, 2026-10-06). An old `#hr` /
    // `#compliance` reaches here when a Quick Docs pin sets the hash after
    // the route — hand it over to People instead of showing Profile.
    function handOff(): boolean {
      const moved = legacyRedirect('/company', window.location.hash);
      if (!moved) return false;
      replaceRoute(moved);
      return true;
    }
    function apply(): void {
      if (handOff()) return;
      const next = hashToCompany();
      setManagement(next.management);
      setMgmtDisk(mgmtDiskFromHash());
      setSection(next.section);
      setDocDisk(next.docDisk);
      setFolderChip(next.folderChip);
      setOpsChip(next.opsChip);
      setVendorId(next.vendorId);
      setCommId(next.commId);
    }
    handOff();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, []);

  useEffect(() => {
    if (section !== 'documents' && section !== 'folder') return;
    const location = clinic ?? visibleClinics()[0];
    if (!location) return;
    const key = companyDriveKey('documents', location);
    if (!peekResource(key)) return;
    void refetchResource(
      key,
      () => fetchCompanyDrivePane('documents', location, { force: true }) as Promise<CompanyDriveData>,
    );
  }, [section, clinic]);

  // Money and Reports read their own data (the store bootstrap fills), so a
  // slow or refused company-profile call must not hold those two tabs
  // hostage — they render with the rest of the page; only Profile waits.
  const ownData = section === 'money' || section === 'reports';

  if (company.status === 'loading' && !ownData) {
    return (
      <div class="main">
        <div class="page-header"><h1>Company</h1></div>
        <EmptyState label="Loading company profiles…" />
      </div>
    );
  }

  if (company.status === 'fault' && !ownData) {
    return (
      <div class="main">
        <div class="page-header"><h1>Company</h1></div>
        <FaultState name="company_load_failed" detail={company.error} />
      </div>
    );
  }

  const profiles = company.status === 'ready' ? company.data.profiles : [];
  const clinics = visibleClinics();
  const activeClinic = activeClinicFrom(clinic, clinics);
  // company.code and location.code are the same four values (docs/
  // company-page-requirements.md) — no join table, no re-derivation.
  const activeProfile = profiles.find((p) => p.company_code === activeClinic) ?? null;
  const showManagement = canSeeManagementTab();
  const managementOn = showManagement && management;
  // Communications is visible to every mapped role, same breadth as
  // Profile/Documents. Intake is OWNER/DIRECTOR only — gated the SAME way
  // the Management tab is (canSeeManagementTab/showManagement), not a
  // second role check, so the two never drift out of sync.
  //
  // Money and Reports sit right after Profile and Operations (owner,
  // 2026-10-06), visible to every role that could open them as top-line
  // pages — the same three mapped roles that open Company; the servers'
  // own per-clinic scoping is unchanged. HR moved to People.
  const sectionTabs: [CompanySection, string][] = [
    ['profile', 'Profile'],
    ['operations', 'Operations'],
    ['money', 'Money'],
    ['reports', 'Reports'],
    ['documents', 'Documents'],
    ['folder', 'My Folder'],
    ['communications', 'Communications'],
  ];
  if (showManagement) sectionTabs.push(['intake', 'Intake']);

  return (
    <div class="main">
      <div class="page-header"><h1>Company</h1></div>

      <ClinicTabs
        clinics={clinics}
        active={managementOn ? 'MANAGEMENT' : activeClinic}
        onSelect={(code) => { setClinic(code); setManagement(false); }}
        after={showManagement ? [{ id: 'MANAGEMENT', label: 'Management' }] : undefined}
        onSelectAfter={() => { setManagement(true); }}
      />

      {managementOn ? (
        <>
          <div class="section-head" data-testid="company-management">
            <div class="section-head-row">
              <h2>Management</h2>
              <div class="section-head-actions">
                <FavoriteStar itemKey="company.management" />
              </div>
            </div>
          </div>
          <div class="company-disks" data-testid="company-management-menu">
            <h3>Disks</h3>
            <div class="mgmt-menu" role="group" aria-label="Disks">
              <button type="button" class="btn" aria-pressed={mgmtDisk === 'files'} onClick={() => setMgmtDisk('files')} data-testid="company-subnav-mgmt-files">Files</button>
              <button type="button" class="btn" aria-pressed={mgmtDisk === 'leases'} onClick={() => setMgmtDisk('leases')} data-testid="company-subnav-mgmt-leases">Leases</button>
              <button type="button" class="btn" aria-pressed={mgmtDisk === 'insurance'} onClick={() => setMgmtDisk('insurance')} data-testid="company-subnav-mgmt-insurance">Insurance</button>
            </div>
          </div>
          {mgmtDisk === 'leases' ? (
            // N-110, moved here from Documents (owner, 2026-09-26). The tab
            // itself is the role gate: only OWNER/DIRECTOR ever render this
            // branch, so a manager typing #leases lands on their clinic page.
            <CompanyLeases pinKey="company.leases" />
          ) : mgmtDisk === 'insurance' ? (
            // Same shape, same evening (owner, 2026-09-26: "Let's get this
            // into a disk called Insurance under Company and Management …
            // same as we did with Leases"). The tab itself is the role gate.
            <CompanyInsurance pinKey="company.insurance" />
          ) : (
            <CompanyDrivePane pane="management" />
          )}
        </>
      ) : (
        <>
          <div class="clinic-row" data-testid="company-section-row">
            <div class="cadence-tabs company-section-tabs" role="tablist" aria-label="Company section">
              {sectionTabs.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={section === id}
                  class={section === id ? 'tab active' : 'tab'}
                  onClick={() => {
                    setSection(id);
                    setVendorId(null);
                    setCommId(null);
                    if (id === 'documents') setDocDisk(FIRST_DOC_DISK);
                    if (id === 'folder') setFolderChip('files');
                    if (id === 'operations') {
                      setOpsChip('wufoo');
                      window.location.hash = 'operations';
                    }
                    if (id === 'money') window.location.hash = 'money';
                    if (id === 'reports') window.location.hash = 'reports';
                    if (id === 'communications') window.location.hash = 'communications';
                    if (id === 'intake') window.location.hash = 'intake';
                  }}
                  data-testid={`company-subnav-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {!NO_DOC_SEARCH.has(section)
              && !(section === 'folder' && folderChip !== 'files') ? (
              <div class="clinic-row-trailing">
                <input
                  type="search"
                  class="company-doc-search"
                  data-testid="company-doc-search"
                  placeholder="Search company folder"
                  value={docQuery}
                  onInput={(e) => setDocQuery((e.target as HTMLInputElement).value)}
                />
              </div>
            ) : null}
          </div>

          {section === 'profile' ? (
            profiles.length === 0 ? (
              <EmptyState label="No company profiles loaded." />
            ) : activeProfile === null ? (
              <EmptyState label="No profile on file for this clinic." />
            ) : (
              <CompanyProfileCard key={activeProfile.company_code} profile={activeProfile} />
            )
          ) : null}

          {isSearchActive(docQuery) && !NO_DOC_SEARCH.has(section)
            && !(section === 'folder' && folderChip !== 'files') ? (
            <CompanyDrivePane
              pane="documents"
              location={activeClinic}
              query={docQuery}
            />
          ) : null}

          {!isSearchActive(docQuery) && section === 'folder' ? (
            <>
              <div class="company-disks" data-testid="company-folder-menu">
                <h3>My Folder</h3>
                <div class="mgmt-menu" role="group" aria-label="My Folder">
                  {FOLDER_CHIPS.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      class="btn"
                      aria-pressed={folderChip === chip.id}
                      onClick={() => {
                        setFolderChip(chip.id);
                        window.location.hash = chip.id === 'files' ? 'folder' : chip.id;
                      }}
                      data-testid={`company-folder-chip-${chip.id}`}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
              {folderChip === 'files' ? (
                <CompanyDrivePane
                  pane="documents"
                  location={activeClinic}
                  disk={MY_FOLDER_DISK}
                />
              ) : null}
              {folderChip === 'bonus' ? <BonusTracker clinic={activeClinic} /> : null}
            </>
          ) : null}

          {!isSearchActive(docQuery) && section === 'documents' ? (
            <>
              <div class="company-disks" data-testid="company-documents-menu">
                <h3>Disks</h3>
                <div class="mgmt-menu" role="group" aria-label="Disks">
                  {DOC_DISKS.map((disk) => (
                    <button
                      key={disk.id}
                      type="button"
                      class="btn"
                      aria-pressed={docDisk === disk.id}
                      onClick={() => setDocDisk(disk.id)}
                      data-testid={`company-subnav-doc-${disk.id}`}
                    >
                      {disk.label}
                    </button>
                  ))}
                </div>
              </div>
              {docDisk === 'licenses' ? (
                // N-100 (owner, 2026-09-10): "Documents → Licenses shows the
                // table only (drop the Drive folder card there; keep 'Open
                // folder' in the table header)" — CompanyLicenses is the
                // whole story on this disk now; the generic Drive file-list
                // card never mounts alongside it.
                <CompanyLicenses key={activeClinic} clinic={activeClinic} pinKey="company.licenses" />
              ) : (
                <CompanyDrivePane
                  pane="documents"
                  location={activeClinic}
                  disk={DOC_DISKS.find((d) => d.id === docDisk) ?? DOC_DISKS[0]}
                />
              )}
            </>
          ) : null}

          {section === 'money' ? <MoneyPage embeddedClinic={activeClinic} /> : null}

          {section === 'reports' ? <ReportsPage embeddedClinic={activeClinic} /> : null}

          {section === 'operations' ? (
            <>
              <div class="company-disks" data-testid="company-operations-menu">
                <h3>Operations</h3>
                <div class="mgmt-menu" role="group" aria-label="Operations">
                  {OPS_CHIPS.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      class="btn"
                      aria-pressed={opsChip === chip.id}
                      onClick={() => {
                        setOpsChip(chip.id);
                        window.location.hash = chip.id;
                      }}
                      data-testid={`company-ops-chip-${chip.id}`}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
              {opsChip === 'wufoo' ? <ServicePoints key={activeClinic} clinic={activeClinic} pinKey="company.wufoo" /> : null}
              {opsChip === 'vendors' ? (
                <CompanyVendors
                  pinKey="company.vendors"
                  // Only the clinic selected above — owner, 2026-09-10: with
                  // every visible clinic stacked here, Winter Garden's vendors
                  // looked like they had been loaded into the Villages.
                  clinics={[activeClinic]}
                  vendorId={vendorId}
                  onOpen={(id) => {
                    setVendorId(id);
                    window.location.hash = `vendor-${id}`;
                  }}
                  onBack={() => {
                    setVendorId(null);
                    window.location.hash = 'vendors';
                  }}
                />
              ) : null}
            </>
          ) : null}

          {section === 'communications' ? (
            <CompanyCommunications
              commId={commId}
              onOpen={(id) => {
                setCommId(id);
                window.location.hash = `comm-${id}`;
              }}
              onBack={() => {
                setCommId(null);
                window.location.hash = 'communications';
              }}
            />
          ) : null}

          {/* showManagement re-checked here, not just at the tab: a manager
              typing #intake directly must not mount a component whose only
              fetch would 403 — same defense hashToCompany's `management`
              branch already relies on (`managementOn = showManagement &&
              management`) for a manager typing #management. */}
          {section === 'intake' && showManagement ? <CompanyIntake /> : null}

        </>
      )}
    </div>
  );
}

function CompanyDrivePane(
  { pane, location, query = '', onlySection, disk }:
  {
    pane: 'documents' | 'vendors' | 'management';
    location?: string;
    query?: string;
    onlySection?: string;
    disk?: { label: string; section: string; upload?: ClinicDriveUpload };
  },
): JSX.Element {
  const drive = useResource<CompanyDriveData>(
    companyDriveKey(pane, location),
    () => fetchCompanyDrivePane(pane, location) as Promise<CompanyDriveData>,
  );

  if (drive.status === 'loading') {
    return <EmptyState label="Loading Drive documents…" />;
  }
  if (drive.status === 'fault') {
    return <FaultState name="company_drive_load_failed" detail={drive.error} />;
  }

  if (!drive.data.configured) {
    return <p class="empty">{drive.data.reason}</p>;
  }

  const searchActive = isSearchActive(query);
  const wanted = searchActive ? undefined : (disk?.section ?? onlySection);
  const sections = (drive.data.sections ?? [])
    .filter((s) => (wanted ? s.name === wanted : true))
    .map((s) => {
      const diskMatch = DOC_DISKS.find((d) => d.section === s.name)
        ?? (s.name === MY_FOLDER_DISK.section ? MY_FOLDER_DISK : undefined);
      return {
        ...s,
        name: disk?.label ?? diskMatch?.label ?? s.name,
        upload: disk?.upload ?? diskMatch?.upload ?? null,
        files: searchActive ? s.files.filter((f) => matchesDriveFileName(f.name, query)) : s.files,
      };
    })
    .filter((s) => (searchActive ? s.files.length > 0 : true));

  if (searchActive && sections.length === 0) {
    return <EmptyState label="No documents match that search." />;
  }
  if (sections.length === 0) {
    return <EmptyState label="No documents in this folder." />;
  }

  const canUpload = Boolean(location && canAddClinicDrive(location));

  return (
    <>
      {sections.map((s) => (
        <CompanyDriveSectionCard
          key={s.name}
          section={s}
          canUpload={canUpload && Boolean(s.upload)}
          pane={s.upload}
          location={location}
          pin={s.name === 'Documents' ? <FavoriteStar itemKey="company.documents" /> : null}
        />
      ))}
    </>
  );
}

/** The HR documents pane (NHP plus each clinic's HR Misc). It lives on
 * People's HR tab since 2026-10-06 (owner: "id like to move HR and the SPC as
 * a menu item in People"); it stays in this file because the Company Drive
 * plumbing it shares does. */
export function CompanyHrPane({ query = '' }: { query?: string }): JSX.Element {
  const hr = useResource<CompanyHrData>(COMPANY_HR_KEY, () => fetchCompanyHr() as Promise<CompanyHrData>);
  const [openExtra, setOpenExtra] = useState<string | null>(null);

  if (hr.status === 'loading') {
    return <EmptyState label="Loading HR documents…" />;
  }
  if (hr.status === 'fault') {
    return <FaultState name="company_hr_load_failed" detail={hr.error} />;
  }

  const topics = hr.data.topics ?? [];
  const shown = topics.find((t) => t.name === openExtra) ?? topics[0] ?? null;

  if (!hr.data.configured) {
    return <p class="empty">{hr.data.reason}</p>;
  }
  if (topics.length === 0) {
    return <p class="empty">{hr.data.reason ?? 'the HR folder has no topics yet'}</p>;
  }

  return (
    <>
      {topics.length > 0 ? (
        <div class="mgmt-menu" data-testid="company-hr-chips">
          {topics.map((t) => (
            <button
              key={t.name}
              type="button"
              class="btn"
              aria-pressed={(openExtra ?? topics[0]?.name) === t.name}
              data-testid={`company-hr-chip-${t.name}`}
              onClick={() => setOpenExtra(t.name)}
            >
              {t.name}
            </button>
          ))}
        </div>
      ) : null}
      {shown ? (
        <CompanyHrTopicView
          key={shown.name}
          topic={shown}
          sections={shown.sections}
          query={query}
        />
      ) : null}
    </>
  );
}

function CompanyHrTopicView(
  { topic, sections, query = '' }:
  { topic: CompanyHrTopic; sections: CompanyDriveSection[]; query?: string },
): JSX.Element {
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [printError, setPrintError] = useState<string | null>(null);
  const [printBusy, setPrintBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const canUpload = canAddHrDocuments(topic.name);

  async function pickFile(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploadBusy(true);
    try {
      await uploadHrDocument(topic.name, file);
      await refetchResource<CompanyHrData>(
        COMPANY_HR_KEY,
        () => fetchCompanyHr({ force: true }) as Promise<CompanyHrData>,
      );
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      input.value = '';
      setUploadBusy(false);
    }
  }

  const rows = flattenHrRows(topic.name, sections)
    .filter((row) => (isSearchActive(query) ? matchesDriveFileName(row.title, query) : true));
  const groups = groupNhpRows(rows);
  const files = rows.flatMap((r) => r.files);
  const selected = rows.filter((r) => picked[r.key]).flatMap((r) => r.files);
  const allOn = rows.length > 0 && rows.every((r) => picked[r.key]);

  function toggle(id: string, on: boolean): void {
    setPicked((prev) => ({ ...prev, [id]: on }));
  }

  function toggleAll(): void {
    if (allOn) {
      setPicked({});
      return;
    }
    const next: Record<string, boolean> = {};
    for (const r of rows) next[r.key] = true;
    setPicked(next);
  }

  async function printSelected(): Promise<void> {
    if (!selected.length) {
      setPrintError('pick at least one document');
      return;
    }
    const win = window.open('', 'nhp-print');
    if (!win) {
      setPrintError('the browser blocked the print window — allow pop-ups for this site');
      return;
    }
    win.document.title = 'Print NHP';
    win.document.body.replaceChildren();
    const loading = win.document.createElement('p');
    loading.textContent = 'Loading…';
    win.document.body.appendChild(loading);
    setPrintBusy(true);
    setPrintError(null);
    const pages: { name: string; url: string; kind: 'pdf' | 'image' }[] = [];
    const failed: string[] = [];
    try {
      for (const file of selected) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const { blob, contentType } = await authedGetBlob(
            `/api/v1/company/drive/print?file=${encodeURIComponent(file.id)}`,
          );
          const url = URL.createObjectURL(blob);
          const kind = contentType.includes('pdf') || file.name.toLowerCase().endsWith('.pdf')
            ? 'pdf'
            : contentType.startsWith('image/')
              ? 'image'
              : null;
          if (!kind) {
            URL.revokeObjectURL(url);
            failed.push(file.name);
            continue;
          }
          pages.push({ name: file.name, url, kind });
        } catch (err) {
          failed.push(err instanceof ApiError ? `${file.name}: ${err.message}` : file.name);
        }
      }
      if (!pages.length) {
        win.close();
        setPrintError(failed.length ? `could not print: ${failed.join('; ')}` : 'could not load those files');
        return;
      }
      win.document.body.replaceChildren();
      for (const page of pages) {
        if (page.kind === 'pdf') {
          const embed = win.document.createElement('embed');
          embed.src = page.url;
          embed.type = 'application/pdf';
          embed.setAttribute('style', 'width:100%;min-height:90vh;page-break-after:always');
          win.document.body.appendChild(embed);
        } else {
          const img = win.document.createElement('img');
          img.src = page.url;
          img.alt = page.name;
          img.setAttribute('style', 'max-width:100%;page-break-after:always');
          win.document.body.appendChild(img);
        }
      }
      win.focus();
      win.print();
      if (failed.length) setPrintError(`printed what we could. skipped: ${failed.join('; ')}`);
    } finally {
      setPrintBusy(false);
    }
  }

  return (
    <section class="card" data-testid="company-hr">
      <div class="section-head">
        <div class="section-head-row">
          <h2>{topic.name}</h2>
          <div class="section-head-actions">
            <OpenFolderLink url={topic.folderUrl} label={topic.name} />
            {canUpload ? (
              <label class="btn" data-testid={`company-hr-upload-label-${topic.name}`}>
                {uploadBusy ? 'Uploading…' : 'Add a document'}
                <input
                  type="file" class="visually-hidden"
                  aria-label={`Add a document to ${topic.name}`}
                  data-testid={`company-hr-upload-${topic.name}`}
                  disabled={uploadBusy}
                  onChange={(ev: Event) => void pickFile(ev)}
                />
              </label>
            ) : null}
            <button type="button" class="btn" data-testid="company-nhp-select-all" onClick={toggleAll} disabled={!files.length}>
              {allOn ? 'Clear all' : 'Select all'}
            </button>
            <button
              type="button" class="btn primary" data-testid="company-nhp-print"
              onClick={() => void printSelected()}
              disabled={printBusy || !files.length}
            >
              {printBusy ? 'Preparing…' : 'Print selected'}
            </button>
          </div>
        </div>
        <p>{topic.name} from the All Managers drive.</p>
      </div>
      {printError ? <small class="bad" role="alert">{printError}</small> : null}
      {uploadError ? <small class="bad" role="alert">{uploadError}</small> : null}
      {!topic.configured ? (
        <p class="empty">{topic.reason}</p>
      ) : rows.length === 0 ? (
        <p class="empty">{topic.reason ?? `the ${topic.name} folder has no documents yet`}</p>
      ) : (
        <div data-testid={`company-nhp-table-${topic.name}`}>
          {groups.map((group, index) => (
            <div key={group.name || `loose-${index}`} class="nhp-section">
              {group.name ? (
                <div class="section-head">
                  <h2>{nhpDisplayTitle(group.name, index)}</h2>
                </div>
              ) : null}
              <ul class="nhp-pack">
                {group.rows.map((row) => {
                  const fileId = row.files[0]?.id;
                  const openUrl = row.webViewLink || row.folderUrl;
                  return (
                    <li
                      key={row.key}
                      class="nhp-pack-row"
                      data-testid={fileId ? `company-nhp-file-${fileId}` : `company-nhp-row-${row.key}`}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Print ${row.title}`}
                        checked={Boolean(picked[row.key])}
                        disabled={!row.files.length}
                        onChange={(ev: Event) => toggle(row.key, (ev.target as HTMLInputElement).checked)}
                      />
                      <span class="nhp-title">{nhpDisplayName(row.title)}</span>
                      {openUrl ? (
                        <a
                          class="drive-mark"
                          href={openUrl}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Open ${row.title} in Google Drive`}
                          title={`Open ${row.title} in Google Drive`}
                        >
                          <span class="sr-only">Open in Google Drive</span>
                        </a>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CompanyDriveSectionCard(
  { section: s, canUpload = false, pane, location, pin }:
  {
    section: CompanyDriveSection;
    canUpload?: boolean;
    pin?: JSX.Element | null;
    pane?: ClinicDriveUpload | null;
    location?: string;
  },
): JSX.Element {
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const sorted = useTableSort(s.files, {
    file: (f) => f.name,
    modified: (f) => f.modifiedTime,
  });

  async function pickFile(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !pane || !location) return;
    setUploadError(null);
    setUploadBusy(true);
    try {
      await uploadClinicDocument(pane, location, file);
      await refetchResource(
        companyDriveKey('documents', location),
        () => fetchCompanyDrivePane('documents', location, { force: true }) as Promise<CompanyDriveData>,
      );
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      input.value = '';
      setUploadBusy(false);
    }
  }

  async function removeFile(fileId: string): Promise<void> {
    if (!pane || !location) return;
    setUploadError(null);
    setDeletingId(fileId);
    try {
      await deleteClinicDocument(pane, location, fileId);
      await dropDriveFile(location, fileId);
      await refetchResource(
        companyDriveKey('documents', location),
        () => fetchCompanyDrivePane('documents', location, { force: true }) as Promise<CompanyDriveData>,
      );
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingId(null);
    }
  }

  async function openFile(fileId: string): Promise<void> {
    if (!pane || !location) return;
    setUploadError(null);
    try {
      const url = await openClinicDocument(pane, location, fileId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      await dropDriveFile(location, fileId);
      setUploadError('That file is gone from Drive.');
    }
  }

  return (
    <section class="card" data-testid={`company-drive-section-${s.name}`}>
      <div class="section-head">
        <div class="section-head-row">
          <h2>{s.name}</h2>
          <div class="section-head-actions">
            {pin ?? null}
            <OpenFolderLink url={s.folderUrl} label={s.name} />
            {canUpload ? (
              <label class="btn" data-testid={`company-drive-upload-label-${s.name}`}>
                {uploadBusy ? 'Uploading…' : 'Add a document'}
                <input
                  type="file"
                  class="visually-hidden"
                  aria-label={`Add a document to ${s.name}`}
                  data-testid={`company-drive-upload-${s.name}`}
                  disabled={uploadBusy}
                  onChange={(ev: Event) => void pickFile(ev)}
                />
              </label>
            ) : null}
          </div>
        </div>
      </div>
      {uploadError ? <small class="bad" role="alert">{uploadError}</small> : null}
      {s.files.length === 0 ? (
        <p class="empty">{s.reason ?? 'No documents in this folder.'}</p>
      ) : (
        <div class="table-wrap">
          <table class="company-documents" data-testid="company-documents">
            <thead>
              <tr>
                <SortableTh label="File" sortKey="file" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Modified" sortKey="modified" state={sorted.state} onSort={sorted.toggle} />
                {canUpload ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((f) => (
                <tr key={f.id} data-testid={`company-drive-file-${f.id}`}>
                  <td>
                    {pane && location ? (
                      <button
                        type="button"
                        class="link doc-link"
                        data-testid={`company-drive-open-${f.id}`}
                        onClick={() => void openFile(f.id)}
                      >
                        {f.name}
                      </button>
                    ) : f.name}
                  </td>
                  <td>{f.modifiedTime ? formatDate(f.modifiedTime) : EM_DASH}</td>
                  {canUpload ? (
                    <td>
                      <button
                        type="button"
                        class="btn"
                        data-testid={`company-drive-delete-${f.id}`}
                        disabled={deletingId === f.id}
                        onClick={() => void removeFile(f.id)}
                      >
                        {deletingId === f.id ? 'Removing…' : 'Remove'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- the profile card ---------------------------------------------------------

/** Build the initial edit-form state from the RAW profile — never the
 * displayed (em-dash-substituted) value. Requirements doc §1: prefilling
 * from the displayed value is a bug that once made an empty field prefill
 * with an em dash. */
function rawFormValues(profile: CompanyProfile): Record<ProfileFieldKey, string> {
  const out = {} as Record<ProfileFieldKey, string>;
  for (const key of ALL_EDITABLE_FIELDS) {
    out[key] = profile[key] ?? '';
  }
  return out;
}

function CompanyProfileCard({ profile }: { profile: CompanyProfile }): JSX.Element {
  // The server saves a profile for owner/director only (src/company.js
  // isCompanyRole) — a manager is not offered a button that cannot save.
  const role = session.value.me?.role;
  const canEditProfile = role === 'OWNER' || role === 'DIRECTOR';
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<ProfileFieldKey, string>>(() => rawFormValues(profile));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit(): void {
    setForm(rawFormValues(profile));
    setError(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    setForm(rawFormValues(profile));
    setError(null);
    setEditing(false);
  }

  async function save(): Promise<void> {
    // Only changed fields go out — the server whitelist would drop the rest
    // anyway, but sending a no-op patch of 20 unchanged fields for every save
    // is not honest about what actually changed.
    const patch: Record<string, string> = {};
    const cleared: string[] = [];
    for (const key of ALL_EDITABLE_FIELDS) {
      const raw = profile[key] ?? '';
      if (form[key] === raw) continue;
      // EMPTYING A FIELD IS NOT A SAVE. The server treats absent and empty
      // the same (src/company.js: an empty value is skipped, never written),
      // so sending a blank would return 200 and change nothing — a save that
      // reports success and silently does nothing is the worst outcome
      // available (INV-9). Named and refused here instead.
      if (form[key].trim() === '' && raw !== '') { cleared.push(key); continue; }
      patch[key] = form[key];
    }
    if (cleared.length > 0) {
      setError('A field cannot be emptied yet — put the value back, or press Cancel.');
      return;
    }
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authedWrite('PATCH', `/api/v1/company/${profile.company_code}`, patch);
      await refetchResource<CompanyResponse>(COMPANY_KEY, () => authedGet('/api/v1/company'));
      setEditing(false);
    } catch (err) {
      // INV-9: the server's own refusal text ("nothing to change", "not
      // found"), never a generic "Something went wrong".
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section class="card company-profile-card" data-testid={`company-profile-${profile.company_code}`}>
      {/* Edit profile at the right end of the heading's own line (owner,
          2026-08-16) — `.section-head-row` / `.section-head-actions`, the
          one shared rule, not a page-local flex declaration. */}
      <div class="section-head">
        <div class="section-head-row">
          <h2>{profile.legal_name}</h2>
          <div class="section-head-actions">
            <FavoriteStar itemKey="company.profile" />
            {editing ? (
              <button type="button" class="btn" onClick={cancelEdit} disabled={busy}>Cancel</button>
            ) : canEditProfile ? (
              <button type="button" class="btn" onClick={startEdit}>Edit profile</button>
            ) : null}
            {editing && (
              <button type="button" class="btn primary" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
        <p>Updated {formatDate(profile.updated_at)}</p>
      </div>
      {error ? <small class="bad" role="alert">{error}</small> : null}

      {PROFILE_GROUPS.map((group) => (
        <ProfileGroup
          key={group.title}
          title={group.title}
          fields={group.fields}
          profile={profile}
          editing={editing}
          form={form}
          onChange={(key, value) => setForm((f) => ({ ...f, [key]: value }))}
        />
      ))}

      {/* Owner, 2026-08-17: "managers shouldnt see banking account info on the
          Company profile but they should see rest. Candace and i see all."
          The server is what enforces it — a manager's GET carries no `banking`
          key at all (src/company.js) — and this is the matching UI: no empty
          section, no Reveal button that could only ever 403. */}
      {profile.banking ? <CompanyBankingSection profile={profile} /> : null}

      <div class="section-head">
        <h2>Notes</h2>
      </div>
      {editing ? (
        <textarea
          class="company-notes-input"
          aria-label="Notes"
          value={form.notes}
          onInput={(e) => setForm((f) => ({ ...f, notes: (e.target as HTMLTextAreaElement).value }))}
        />
      ) : (
        <p class="company-notes">{profile.notes ?? EM_DASH}</p>
      )}
    </section>
  );
}

function ProfileGroup(
  {
    title, fields, profile, editing, form, onChange,
  }: {
    title: string;
    fields: [ProfileFieldKey, string][];
    profile: CompanyProfile;
    editing: boolean;
    form: Record<ProfileFieldKey, string>;
    onChange: (key: ProfileFieldKey, value: string) => void;
  },
): JSX.Element {
  return (
    <div class="company-profile-group" data-testid={`company-group-${title}`}>
      <div class="section-head"><h2>{title}</h2></div>
      <dl class="company-field-list">
        {fields.map(([key, label]) => (
          <div class="company-field" key={key}>
            <dt>{label}</dt>
            <dd>
              {editing ? (
                <input
                  type={DATE_FIELDS.has(key) ? 'date' : 'text'}
                  aria-label={label}
                  value={form[key]}
                  onInput={(e) => onChange(key, (e.target as HTMLInputElement).value)}
                />
              ) : (
                displayValue(profile[key], DATE_FIELDS.has(key))
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function displayValue(raw: string | null, isDate: boolean): string | JSX.Element {
  if (raw === null || raw === '') return EM_DASH;
  return isDate ? formatDate(raw) : raw;
}

// --- banking (R-17) -----------------------------------------------------------

/**
 * Banking is deliberately NOT part of `ProfileGroup`/`ALL_EDITABLE_FIELDS`:
 * the PATCH whitelist (src/company.js COMPANY_PROFILE_FIELDS) has no
 * `bank_routing`/`bank_account` at all, so this section is never editable
 * here and never pretends to be. The mask comes back with every GET; the
 * real numbers come back only from a POST reveal, which IS the logged read
 * (R-17) — so the click matters, not just the display.
 */
function CompanyBankingSection({ profile }: { profile: CompanyProfile }): JSX.Element {
  const [revealed, setRevealed] = useState<{ bankRouting: string; bankAccount: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Masks after an edit (the page's own cached profile is not rewritten).
  const [newMasks, setNewMasks] = useState<BankingMasks | null>(null);
  const masks = newMasks ?? profile.banking;

  async function reveal(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await authedWrite<{ companyCode: string; bankRouting: string; bankAccount: string }>(
        'POST',
        `/api/v1/company/${profile.company_code}/banking`,
      );
      setRevealed({ bankRouting: body.bankRouting, bankAccount: body.bankAccount });
    } catch (err) {
      // A clinic with nothing on file 404s with a readable message — render
      // that message in place, never a fault screen (INV-9).
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const routing = revealed ? padRoutingNumber(revealed.bankRouting) : null;

  return (
    <div class="company-profile-group" data-testid="company-banking">
      <div class="section-head"><h2>Banking</h2></div>
      <dl class="company-field-list">
        <div class="company-field">
          <dt>Routing number</dt>
          <dd>
            {revealed && routing ? (
              <>
                {routing.value}
                {routing.warning && (
                  <small class="warn" data-testid="routing-warning">
                    {' '}longer than 9 digits — shown as recorded, not corrected
                  </small>
                )}
              </>
            ) : (
              masks?.bank_routing ?? EM_DASH
            )}
          </dd>
        </div>
        <div class="company-field">
          <dt>Account number</dt>
          <dd>{revealed ? revealed.bankAccount : (masks?.bank_account ?? EM_DASH)}</dd>
        </div>
      </dl>
      <CompanyBankingEdit companyCode={profile.company_code}
        onSaved={(m) => { setNewMasks(m); setRevealed(null); }} />
      {revealed ? (
        <small class="muted">This reveal was recorded against your account.</small>
      ) : (
        <button type="button" class="btn" disabled={busy} onClick={() => void reveal()}>
          {busy ? 'Revealing…' : 'Show'}
        </button>
      )}
      {error ? <small class="bad" role="alert">{error}</small> : null}
    </div>
  );
}
